// src/pages/Reports.js

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  FileText,
  FileSpreadsheet,
  Sparkles,
  TrendingUp,
  Briefcase,
  User,
  Navigation,
Route,
  ArrowRight,
  ChevronRight,
  ChevronUp,
  X,
  AlertTriangle,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import {
  reports as reportsApi,
  expenses as expensesApi,
  vehicles as vehiclesApi,
  downloadExport,
} from "../services/api";

// Brand colours
const PRIMARY = "#00ACC1";
const PRIMARY_DK = "#00838f";
const BIZ_COLOR = "#FF9800";
const BIZ_LIGHT = "#FFB74D";
const PRIV_COLOR = "#4CAF50";
const PRIV_LIGHT = "#81C784";

// Helpers
const fmtKm = (km) => parseFloat(km || 0).toFixed(1);

const monthName = (str) => {
  if (!str) return "";
  const [, m] = str.split("-");
  return (
    [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][parseInt(m) - 1] || m
  );
};

const currentTaxYear = () => {
  const now = new Date();
  return now.getMonth() >= 2 ? now.getFullYear() + 1 : now.getFullYear();
};

const taxYearLabel = (year) => `Mar ${year - 1} – Feb ${year}`;

const taxYearRange = (year) => ({
  date_from: `${year - 1}-03-01`,
  date_to: year % 4 === 0 ? `${year}-02-29` : `${year}-02-28`,
});

// Back to top
const BackToTop = () => {
  const [v, setV] = useState(false);
  useEffect(() => {
    const fn = () => setV(window.scrollY > 400);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg hover:scale-110 transition-all duration-500 ${v ? "translate-y-0 opacity-100" : "translate-y-20 opacity-0 pointer-events-none"}`}
      style={{
        background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DK})`,
        boxShadow: "0 4px 14px rgba(0,172,193,0.4)",
      }}
    >
      <ChevronUp size={24} />
    </button>
  );
};

// Card
const Card = ({ children, className = "", theme }) => (
  <div
    className={`rounded-2xl border transition-all ${className}`}
    style={{
      backgroundColor: theme.card,
      borderColor: theme.border,
      boxShadow: theme.isDark
        ? "0 4px 24px rgba(0,0,0,0.35)"
        : "0 2px 16px rgba(0,0,0,0.06)",
    }}
  >
    {children}
  </div>
);

// Stat card
const StatCard = ({ icon: Icon, label, value, sub, color, theme }) => (
  <Card theme={theme} className="p-5 hover:scale-[1.01] cursor-default">
    <div className="flex flex-col items-center text-center">
      <div
        className="p-2.5 rounded-xl mb-3"
        style={{ backgroundColor: `${color}18` }}
      >
        <Icon size={18} style={{ color }} strokeWidth={1.8} />
      </div>
      <p
        className="text-2xl font-bold tracking-tight"
        style={{ color: theme.text }}
      >
        {value}
      </p>
      <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
        {label}
      </p>
      {sub && (
        <p
          className="text-xs mt-1 font-medium"
          style={{ color: theme.textSecondary }}
        >
          {sub}
        </p>
      )}
    </div>
  </Card>
);

// Monthly bar chart
const MonthlyBarChart = ({ data, theme }) => {
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, content: null });

  if (!data || !data.length)
    return (
      <p
        className="text-xs py-8 text-center"
        style={{ color: theme.textMuted }}
      >
        No monthly data for this period
      </p>
    );

  const max = Math.max(
    ...data.map(
      (d) => parseFloat(d.business_km || 0) + parseFloat(d.private_km || 0),
    ),
    1,
  );
  const barW = Math.min(48, Math.max(20, 700 / data.length - 10));

  return (
    <div className="relative overflow-x-auto">
      {tip.visible && (
        <div
          className="absolute z-50 pointer-events-none"
          style={{
            left: tip.x,
            top: tip.y - 10,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div
            className="px-3 py-2 rounded-xl text-xs font-medium shadow-lg"
            style={{
              backgroundColor: "#1C1C1E",
              color: "#f5f5f5",
              boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            }}
          >
            {tip.content}
          </div>
          <div
            className="w-2 h-2 rotate-45 mx-auto -mt-1"
            style={{ backgroundColor: "#1C1C1E" }}
          />
        </div>
      )}

      <div
        className="flex items-end gap-2 justify-center"
        style={{ height: 225, minWidth: data.length * (barW + 10) }}
      >
        {data.map((d, i) => {
          const bKm = parseFloat(d.business_km || 0);
          const pKm = parseFloat(d.private_km || 0);
          return (
            <div
              key={i}
              className="flex flex-col items-center"
              style={{ width: barW }}
            >
              <div
                className="w-full flex flex-col items-stretch justify-end cursor-pointer"
                style={{ height: 200 }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const par = e.currentTarget
                    .closest(".relative")
                    .getBoundingClientRect();
                  setTip({
                    visible: true,
                    x: rect.left - par.left + rect.width / 2,
                    y: rect.top - par.top,
                    content: (
                      <div className="space-y-1">
                        <p className="font-semibold">{monthName(d.month)}</p>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: BIZ_COLOR }}
                          />
                          <span>Business: {bKm.toFixed(1)} km</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: PRIV_COLOR }}
                          />
                          <span>Private: {pKm.toFixed(1)} km</span>
                        </div>
                        <p style={{ opacity: 0.6 }}>
                          Total: {(bKm + pKm).toFixed(1)} km
                        </p>
                      </div>
                    ),
                  });
                }}
                onMouseLeave={() => setTip((p) => ({ ...p, visible: false }))}
              >
                {bKm > 0 && (
                  <div
                    className="w-full rounded-t-lg transition-all duration-500"
                    style={{
                      height: `${(bKm / max) * 100}%`,
                      background: `linear-gradient(180deg, ${BIZ_LIGHT}, ${BIZ_COLOR})`,
                      minHeight: 3,
                    }}
                  />
                )}
                {pKm > 0 && (
                  <div
                    className="w-full rounded-b-lg transition-all duration-500"
                    style={{
                      height: `${(pKm / max) * 100}%`,
                      background: `linear-gradient(180deg, ${PRIV_LIGHT}, ${PRIV_COLOR})`,
                      minHeight: 3,
                    }}
                  />
                )}
              </div>
              <span
                className="mt-1.5"
                style={{ fontSize: 10, color: theme.textMuted }}
              >
                {monthName(d.month)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Donut chart
const DonutChart = ({ value1, value2, label1, label2, theme }) => {
  const total = value1 + value2;
  if (!total)
    return (
      <p
        className="text-xs text-center py-4"
        style={{ color: theme.textMuted }}
      >
        No data
      </p>
    );

  const pct1 = value1 / total;
  const r = 52,
    cx = 65,
    cy = 65,
    sw = 16,
    circ = 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center">
      <svg width={130} height={130} viewBox="0 0 130 130">
        <defs>
          <linearGradient id="rpt1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={BIZ_LIGHT} />
            <stop offset="100%" stopColor={BIZ_COLOR} />
          </linearGradient>
          <linearGradient id="rpt2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={PRIV_LIGHT} />
            <stop offset="100%" stopColor={PRIV_COLOR} />
          </linearGradient>
        </defs>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={theme.border}
          strokeWidth={sw}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="url(#rpt1)"
          strokeWidth={sw}
          strokeDasharray={`${circ * pct1} ${circ * (1 - pct1)}`}
          strokeDashoffset={circ * 0.25}
          strokeLinecap="round"
          style={{
            transition: "stroke-dasharray 0.8s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="url(#rpt2)"
          strokeWidth={sw}
          strokeDasharray={`${circ * (1 - pct1)} ${circ * pct1}`}
          strokeDashoffset={circ * 0.25 - circ * pct1}
          strokeLinecap="round"
          style={{
            transition: "stroke-dasharray 0.8s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
        <text
          x={cx}
          y={cy - 5}
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          fill={theme.text}
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          fontSize="10"
          fill={theme.textMuted}
        >
          trips
        </text>
      </svg>
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: BIZ_COLOR }}
          />
          <span className="text-xs" style={{ color: theme.textSecondary }}>
            {label1} ({value1})
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: PRIV_COLOR }}
          />
          <span className="text-xs" style={{ color: theme.textSecondary }}>
            {label2} ({value2})
          </span>
        </div>
      </div>
    </div>
  );
};

// Export modal
// Adds an optional "Vehicle for this report" section (name, reg, starting odometer)
// and a custom date-range option alongside the tax-year presets. All temp state —
// nothing is saved; blank fields = original behaviour.
const ExportModal = ({ onClose, theme, isPro, user, vehicleList }) => {
  const taxYear = currentTaxYear();
  const years = Array.from({ length: 5 }, (_, i) => taxYear - i);

  const cars = vehicleList.filter((v) => v.is_active !== 0);
  const defaultCar = cars.find((v) => v.is_default) || cars[0] || null;

  const [rangeMode, setRangeMode] = useState("taxyear"); // 'taxyear' | 'custom'
  const [selYear, setSelYear] = useState(taxYear);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // A SARS logbook covers one vehicle, so we default to the user's default car.
  // "other" lets them describe an untracked vehicle by hand.
  const [scope, setScope] = useState(defaultCar ? String(defaultCar.id) : "other");

  // Manual details, only used when scope === 'other'
  const [vName, setVName] = useState("");
  const [vReg, setVReg] = useState("");
  const [vOdo, setVOdo] = useState("");

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  // Month/year pickers for the custom range — easier than typing dates,
  // and snaps To to the last day of the chosen month
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const nowYear = new Date().getFullYear();
  const pickYears = Array.from(
    { length: nowYear - 2019 + 1 },
    (_, i) => 2020 + i,
  ).reverse();

  const fromDate = customFrom ? new Date(customFrom + "T00:00:00") : null;
  const toDate = customTo ? new Date(customTo + "T00:00:00") : null;

  const setFromMonthYear = (month, year) =>
    setCustomFrom(`${year}-${String(month + 1).padStart(2, "0")}-01`);

  const setToMonthYear = (month, year) => {
    const last = new Date(year, month + 1, 0).getDate();
    setCustomTo(`${year}-${String(month + 1).padStart(2, "0")}-${last}`);
  };

  const selStyle = {
    flex: 1,
    padding: "8px",
    borderRadius: 10,
    border: `1px solid ${theme.border}`,
    fontSize: 12,
    outline: "none",
    cursor: "pointer",
    textAlign: "center",
    fontWeight: 500,
    backgroundColor: theme.background,
    color: theme.text,
  };

  const selectedCar =
    scope !== "other" ? cars.find((v) => String(v.id) === scope) : null;

  const carLabel = (v) =>
    v.nickname ||
    [v.year, v.make, v.model].filter(Boolean).join(" ") ||
    `Vehicle ${v.id}`;

  const resolveRange = () => {
    if (rangeMode === "custom") {
      return { date_from: customFrom, date_to: customTo };
    }
    return taxYearRange(selYear);
  };

  const handleExport = async (format) => {
    if (!isPro) return;
    const { date_from, date_to } = resolveRange();
    if (!date_from || !date_to) {
      setExportError("Please choose a valid date range.");
      return;
    }
    if (rangeMode === "custom" && date_from > date_to) {
      setExportError("Start date must be before end date.");
      return;
    }
    if (scope === "other" && !vName.trim()) {
      setExportError("Enter a vehicle name for this report.");
      return;
    }
    setExporting(true);
    setExportError("");
    try {
      await downloadExport(format, {
        date_from,
        date_to,
        // tracked vehicle scopes the query; 'other' exports everything in range
        vehicle_id: selectedCar ? selectedCar.id : null,
        userName: user?.display_name || user?.email || "",
        vehicleName: selectedCar ? carLabel(selectedCar) : vName.trim(),
        vehicleReg: selectedCar ? selectedCar.registration || "" : vReg.trim(),
        // tracked vehicles already carry correct per-trip odometer readings
        startingOdometer:
          selectedCar || vOdo.trim() === "" ? null : vOdo.trim(),
      });
      onClose();
    } catch (err) {
      setExportError(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const inputStyle = {
    backgroundColor: theme.background,
    borderColor: theme.border,
    color: theme.text,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full max-w-sm rounded-2xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.card }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold" style={{ color: theme.text }}>
            Export Logbook
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-opacity hover:opacity-70"
            style={{ color: theme.textMuted }}
          >
            <X size={16} />
          </button>
        </div>

        {!isPro ? (
          <div className="text-center py-4">
            <Sparkles
              size={28}
              className="mx-auto mb-3"
              style={{ color: PRIMARY }}
            />
            <p
              className="text-sm font-semibold mb-1"
              style={{ color: theme.text }}
            >
              Pro feature
            </p>
            <p className="text-xs mb-4" style={{ color: theme.textMuted }}>
              Upgrade to Pro to export your SARS-compliant travel logbook.
            </p>
            <Link
              to="/portal/upgrade"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-bold"
              style={{
                background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DK})`,
              }}
            >
              <Sparkles size={14} /> Upgrade to Pro
            </Link>
          </div>
        ) : (
          <>
            {/* Vehicle — first, because a logbook is per vehicle */}
            <label
              className="text-[10px] font-semibold uppercase tracking-wider block mb-1"
              style={{ color: theme.textMuted }}
            >
              Vehicle
            </label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border text-sm outline-none mb-1.5"
              style={inputStyle}
            >
              {cars.map((v) => (
                <option key={v.id} value={String(v.id)}>
                  {carLabel(v)}
                  {v.registration ? ` (${v.registration})` : ""}
                </option>
              ))}
              <option value="other">Other vehicle (not tracked)</option>
            </select>
            <p
              className="text-[11px] mb-4"
              style={{ color: theme.textMuted }}
            >
              {selectedCar
                ? "Only this vehicle's trips are included."
                : "All trips in the range are included, with the details below on the report."}
            </p>

            {/* Range mode toggle */}
            <div
              className="flex rounded-xl p-1 mb-4"
              style={{ backgroundColor: theme.background }}
            >
              {[
                { id: "taxyear", label: "Tax Year" },
                { id: "custom", label: "Custom Range" },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setRangeMode(m.id)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    backgroundColor:
                      rangeMode === m.id ? PRIMARY : "transparent",
                    color: rangeMode === m.id ? "#fff" : theme.textSecondary,
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {rangeMode === "taxyear" ? (
              <>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {years.map((y) => (
                    <button
                      key={y}
                      onClick={() => setSelYear(y)}
                      className="py-2.5 rounded-xl text-xs font-semibold transition-opacity"
                      style={{
                        backgroundColor:
                          selYear === y ? PRIMARY : theme.background,
                        color: selYear === y ? "white" : theme.textSecondary,
                        border: `1px solid ${selYear === y ? PRIMARY : theme.border}`,
                      }}
                    >
                      {y}
                    </button>
                  ))}
                </div>
                <p
                  className="text-[11px] mb-4 text-center"
                  style={{ color: theme.textMuted }}
                >
                  {taxYearLabel(selYear)}
                </p>
              </>
           ) : (
              <>
                {/* From */}
                <div
                  className="p-3 rounded-xl mb-2"
                  style={{
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <p
                    className="text-[10px] font-semibold mb-2"
                    style={{ color: theme.textMuted }}
                  >
                    From
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select
                      value={fromDate ? fromDate.getMonth() : ""}
                      onChange={(e) =>
                        setFromMonthYear(
                          parseInt(e.target.value),
                          fromDate ? fromDate.getFullYear() : nowYear,
                        )
                      }
                      style={selStyle}
                    >
                      <option value="" disabled>
                        Month
                      </option>
                      {MONTHS.map((m, i) => (
                        <option key={i} value={i}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <select
                      value={fromDate ? fromDate.getFullYear() : ""}
                      onChange={(e) =>
                        setFromMonthYear(
                          fromDate ? fromDate.getMonth() : 0,
                          parseInt(e.target.value),
                        )
                      }
                      style={selStyle}
                    >
                      <option value="" disabled>
                        Year
                      </option>
                      {pickYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-center my-2">
                  <ArrowRight
                    size={14}
                    style={{
                      color: theme.textMuted,
                      transform: "rotate(90deg)",
                    }}
                  />
                </div>

                {/* To */}
                <div
                  className="p-3 rounded-xl mb-3"
                  style={{
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <p
                    className="text-[10px] font-semibold mb-2"
                    style={{ color: theme.textMuted }}
                  >
                    To
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select
                      value={toDate ? toDate.getMonth() : ""}
                      onChange={(e) =>
                        setToMonthYear(
                          parseInt(e.target.value),
                          toDate ? toDate.getFullYear() : nowYear,
                        )
                      }
                      style={selStyle}
                    >
                      <option value="" disabled>
                        Month
                      </option>
                      {MONTHS.map((m, i) => (
                        <option key={i} value={i}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <select
                      value={toDate ? toDate.getFullYear() : ""}
                      onChange={(e) =>
                        setToMonthYear(
                          toDate ? toDate.getMonth() : 11,
                          parseInt(e.target.value),
                        )
                      }
                      style={selStyle}
                    >
                      <option value="" disabled>
                        Year
                      </option>
                      {pickYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {customFrom && customTo && (
                  <p
                    className="text-[10px] text-center mb-4"
                    style={{ color: theme.textMuted }}
                  >
                    {new Date(customFrom + "T00:00:00").toLocaleDateString(
                      "en-ZA",
                      { day: "numeric", month: "short", year: "numeric" },
                    )}
                    {" — "}
                    {new Date(customTo + "T00:00:00").toLocaleDateString(
                      "en-ZA",
                      { day: "numeric", month: "short", year: "numeric" },
                    )}
                  </p>
                )}
              </>
            )}

            {/* Manual details for an untracked vehicle */}
            {!selectedCar && (
              <div
                className="rounded-xl p-3.5 mb-4"
                style={{ backgroundColor: theme.background }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-wide mb-1.5"
                  style={{ color: theme.text }}
                >
                  Vehicle details
                </p>
                <p
                  className="text-xs leading-relaxed mb-3"
                  style={{ color: theme.text }}
                >
                  Enter the vehicle and its starting odometer — trip distances
                  roll the odometer forward automatically.
                </p>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={vName}
                    onChange={(e) => setVName(e.target.value)}
                    placeholder="Vehicle name (e.g. Toyota Hilux)"
                    className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                    style={inputStyle}
                  />
                  <input
                    type="text"
                    value={vReg}
                    onChange={(e) => setVReg(e.target.value)}
                    placeholder="Registration (e.g. CA 123 456)"
                    className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    value={vOdo}
                    onChange={(e) => setVOdo(e.target.value)}
                    placeholder="Starting odometer (km)"
                    className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                    style={inputStyle}
                  />
                </div>
              </div>
            )}

            {exportError && (
              <div
                className="mb-3 px-3 py-2.5 rounded-xl text-xs"
                style={{
                  backgroundColor: theme.isDark
                    ? "rgba(239,68,68,0.12)"
                    : "#fef2f2",
                  color: "#f87171",
                }}
              >
                {exportError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleExport("pdf")}
                disabled={exporting}
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all active:scale-95"
                style={{ backgroundColor: PRIMARY }}
              >
                <FileText size={16} />
                {exporting ? "..." : "PDF"}
              </button>
              <button
                onClick={() => handleExport("csv")}
                disabled={exporting}
                className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold disabled:opacity-50 transition-all active:scale-95"
                style={{
                  border: `2px solid ${PRIMARY}40`,
                  color: PRIMARY,
                  backgroundColor: `${PRIMARY}08`,
                }}
              >
                <FileSpreadsheet size={16} />
                {exporting ? "..." : "CSV"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main
export default function Reports() {
  const theme = useTheme();
  const { user } = useAuth();
  const isPro = user?.tier === "pro";

  const taxYear = currentTaxYear();
  const { date_from, date_to } = taxYearRange(taxYear);

  const [summary, setSummary] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [vehicleList, setVehicleList] = useState([]);

  useEffect(() => {
    vehiclesApi.list().then((res) => {
      if (res.success && res.data) setVehicleList(res.data);
    });
  }, []);

  useEffect(() => {
    const params = { date_from, date_to };
    Promise.all([reportsApi.summary(params), expensesApi.summary(params)]).then(
      ([res, expRes]) => {
        if (res.success && res.data) setSummary(res.data);
        if (expRes.success && expRes.data) setExpenseData(expRes.data);
        setLoading(false);
      },
    );
  }, [date_from, date_to]);

  const totals = summary?.totals;

  return (
    <div
      className="relative px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 min-h-screen"
      style={{ backgroundColor: theme.background }}
    >
      <div className="relative z-[1]">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-4 sm:mb-6">
          <div>
            <h1
              className="text-xl sm:text-2xl md:text-3xl font-bold"
              style={{ color: theme.text }}
            >
              Reports
            </h1>
            <p
              className="text-[11px] sm:text-sm mt-0.5 sm:mt-1"
              style={{ color: theme.textMuted }}
            >
              {taxYearLabel(taxYear)} · current SA tax year
            </p>
          </div>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[10px] sm:text-xs font-semibold text-white transition-all hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DK})`,
            }}
          >
            <Download size={13} />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>

        {/* Pro upgrade banner */}
        {!isPro && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-2xl mb-4 sm:mb-6"
            style={{
              backgroundColor: theme.isDark ? `${PRIMARY}18` : "#e0f7fa",
              border: `1px solid ${PRIMARY}25`,
            }}
          >
            <Sparkles size={16} style={{ color: PRIMARY }} />
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-semibold"
                style={{ color: theme.text }}
              >
                Export requires Pro
              </p>
              <p className="text-xs" style={{ color: theme.textMuted }}>
                Upgrade to download your SARS logbook.
              </p>
            </div>
            <Link
              to="/portal/upgrade"
              className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-xl text-white"
              style={{
                background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DK})`,
              }}
            >
              Upgrade
            </Link>
          </div>
        )}

        {/* Unclassified warning */}
        {!loading && totals && parseInt(totals.unclassified_trips) > 0 && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-2xl mb-4 sm:mb-6"
            style={{
              backgroundColor: theme.isDark
                ? "rgba(245,158,11,0.1)"
                : "#fffbeb",
              border: "1px solid rgba(245,158,11,0.2)",
            }}
          >
            <AlertTriangle size={16} style={{ color: "#f59e0b" }} />
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-semibold"
                style={{ color: theme.text }}
              >
                {totals.unclassified_trips} unclassified trip
                {totals.unclassified_trips !== "1" ? "s" : ""}
              </p>
              <p className="text-xs" style={{ color: theme.textMuted }}>
                Classify in the app for accurate SARS reporting.
              </p>
            </div>
            <Link
              to="/portal/trips"
              className="shrink-0 flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-70"
              style={{ color: "#f59e0b" }}
            >
              View <ChevronRight size={12} />
            </Link>
          </div>
        )}

        {/* Stat cards */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-28 rounded-2xl animate-pulse"
                style={{ backgroundColor: theme.card }}
              />
            ))}
          </div>
        ) : (
          totals && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
              <StatCard
                icon={Navigation}
                label="Total Trips"
                value={totals.total_trips}
                color={PRIMARY}
                theme={theme}
              />
              <StatCard
                icon={Route}
                label="Total Distance"
                value={`${fmtKm(totals.total_km)} km`}
                color={PRIMARY}
                theme={theme}
              />
              <StatCard
                icon={Briefcase}
                label="Business"
                value={`${fmtKm(totals.business_km)} km`}
                sub={`${totals.business_trips} trips`}
                color={BIZ_COLOR}
                theme={theme}
              />
              <StatCard
                icon={User}
                label="Private"
                value={`${fmtKm(totals.private_km)} km`}
                sub={`${totals.private_trips} trips`}
                color={PRIV_COLOR}
                theme={theme}
              />
            </div>
          )
        )}

        {/* Monthly chart + donut */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-6">
          <Card theme={theme} className="p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h3
                className="text-sm font-semibold"
                style={{ color: theme.text }}
              >
                Monthly Distance
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{
                      background: `linear-gradient(135deg, ${BIZ_LIGHT}, ${BIZ_COLOR})`,
                    }}
                  />
                  <span className="text-xs" style={{ color: theme.textMuted }}>
                    Business
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{
                      background: `linear-gradient(135deg, ${PRIV_LIGHT}, ${PRIV_COLOR})`,
                    }}
                  />
                  <span className="text-xs" style={{ color: theme.textMuted }}>
                    Private
                  </span>
                </div>
              </div>
            </div>
            {loading ? (
              <div
                className="h-48 rounded-xl animate-pulse"
                style={{ backgroundColor: theme.background }}
              />
            ) : (
              <MonthlyBarChart data={summary?.by_month} theme={theme} />
            )}
          </Card>

          <Card theme={theme} className="p-5 flex flex-col">
            <h3
              className="text-sm font-semibold mb-4"
              style={{ color: theme.text }}
            >
              Trip Split
            </h3>
            <div className="flex-1 flex items-center justify-center">
              {loading ? (
                <div
                  className="w-32 h-32 rounded-full animate-pulse"
                  style={{ backgroundColor: theme.background }}
                />
              ) : (
                <DonutChart
                  value1={parseInt(totals?.business_trips || 0)}
                  value2={parseInt(totals?.private_trips || 0)}
                  label1="Business"
                  label2="Private"
                  theme={theme}
                />
              )}
            </div>
          </Card>
        </div>

        {/* Distance breakdown + Top reasons */}
        {!loading && totals && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-6">
            {/* Distance breakdown */}
            <Card theme={theme} className="p-5">
              <h3
                className="text-sm font-semibold mb-4"
                style={{ color: theme.text }}
              >
                Distance Breakdown
              </h3>
              <div className="text-center mb-4">
                <p className="text-xs" style={{ color: theme.textMuted }}>
                  Total Driven
                </p>
                <p
                  className="text-3xl font-bold mt-1"
                  style={{ color: theme.text }}
                >
                  {fmtKm(totals.total_km)} km
                </p>
              </div>
              <div
                className="rounded-xl p-3 space-y-2.5"
                style={{ backgroundColor: theme.background }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: BIZ_COLOR }}
                    />
                    <span
                      className="text-xs"
                      style={{ color: theme.textSecondary }}
                    >
                      Business
                    </span>
                  </div>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: BIZ_COLOR }}
                  >
                    {fmtKm(totals.business_km)} km
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: PRIV_COLOR }}
                    />
                    <span
                      className="text-xs"
                      style={{ color: theme.textSecondary }}
                    >
                      Private
                    </span>
                  </div>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: PRIV_COLOR }}
                  >
                    {fmtKm(totals.private_km)} km
                  </span>
                </div>
                {parseInt(totals.unclassified_trips) > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: theme.textMuted }}
                      />
                      <span
                        className="text-xs"
                        style={{ color: theme.textSecondary }}
                      >
                        Unclassified
                      </span>
                    </div>
                    <span
                      className="text-xs font-semibold"
                      style={{ color: theme.textMuted }}
                    >
                      {totals.unclassified_trips} trips
                    </span>
                  </div>
                )}
                <div
                  className="pt-2"
                  style={{ borderTop: `1px solid ${theme.border}` }}
                >
                  <div className="flex justify-between items-center">
                    <span
                      className="text-xs font-medium"
                      style={{ color: theme.text }}
                    >
                      Total
                    </span>
                    <span
                      className="text-sm font-bold"
                      style={{ color: theme.text }}
                    >
                      {fmtKm(totals.total_km)} km
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Top reasons */}
            <Card theme={theme} className="p-5 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-sm font-semibold"
                  style={{ color: theme.text }}
                >
                  Top Reasons
                </h3>
                <Link
                  to="/portal/trips"
                  className="text-xs font-medium"
                  style={{ color: PRIMARY }}
                >
                  View trips
                </Link>
              </div>
              {summary?.by_reason?.length > 0 ? (
                <div className="space-y-3">
                  {summary.by_reason.slice(0, 8).map((r, i) => {
                    const isBiz = r.category === "business";
                    const color = isBiz ? BIZ_COLOR : PRIV_COLOR;
                    const maxKm = parseFloat(
                      summary.by_reason[0].total_km || 1,
                    );
                    const pct = Math.round(
                      (parseFloat(r.total_km) / maxKm) * 100,
                    );
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                            <div
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: color }}
                            />
                            <span
                              className="text-xs font-medium truncate"
                              style={{ color: theme.text }}
                            >
                              {r.reason || "No reason"}
                            </span>
                          </div>
                          <span
                            className="text-xs shrink-0"
                            style={{ color: theme.textMuted }}
                          >
                            {parseFloat(r.total_km).toFixed(1)} km · {r.trips}{" "}
                            trips
                          </span>
                        </div>
                        <div
                          className="h-2 rounded-full overflow-hidden"
                          style={{ backgroundColor: theme.background }}
                        >
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <p
                    className="text-xs text-center"
                    style={{ color: theme.textMuted }}
                  >
                    No classified trips yet — classify trips in the app to see
                    reasons here.
                  </p>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Expenses summary */}
        {!loading && expenseData && expenseData.total_expenses > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5 mb-6">
            <Card theme={theme} className="p-5 lg:col-span-3">
              <h3
                className="text-sm font-semibold mb-4"
                style={{ color: theme.text }}
              >
                Expenses
              </h3>
              <div className="text-center mb-5">
                <p className="text-3xl font-bold" style={{ color: theme.text }}>
                  R{expenseData.total.toFixed(2)}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.textMuted }}>
                  {expenseData.total_expenses} expense
                  {expenseData.total_expenses !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(expenseData.by_category ?? []).map((cat, i) => {
                  const pct =
                    expenseData.total > 0 ? cat.total / expenseData.total : 0;
                  return (
                    <div
                      key={i}
                      className="rounded-xl p-3"
                      style={{ backgroundColor: theme.background }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span
                          className="text-xs font-semibold"
                          style={{ color: theme.text }}
                        >
                          {cat.category.charAt(0).toUpperCase() +
                            cat.category.slice(1)}
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: theme.textMuted }}
                        >
                          R{cat.total.toFixed(2)} ({cat.count})
                        </span>
                      </div>
                      <div
                        className="h-2 rounded-full overflow-hidden"
                        style={{ backgroundColor: theme.border }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.max(pct * 100, 2)}%`,
                            backgroundColor: PRIMARY,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}
      </div>

      <BackToTop />

      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          theme={theme}
         isPro={isPro}
          user={user}
          vehicleList={vehicleList}
        />
      )}
    </div>
  );
}